import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Bell, BellOff, Mail, RefreshCw, Trash2, Send, 
  FileText, Settings, AlertTriangle, CheckCircle, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserNotifications() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  
  // Filter states
  const [appFilter, setAppFilter] = useState('active'); // 'active' or 'completed'
  const [implFilter, setImplFilter] = useState('active'); // 'active' or 'completed'

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/notification-settings`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleUserNotifications = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/pause-notifications?paused=${!data.user.notifications_paused}`);
      toast.success(data.user.notifications_paused ? 'Οι ειδοποιήσεις ενεργοποιήθηκαν' : 'Οι ειδοποιήσεις σταμάτησαν');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleClearUserQueue = async () => {
    if (!window.confirm(`Διαγραφή ${data.email_queue.pending} emails από την ουρά;`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/clear-email-queue`);
      toast.success('Η ουρά καθαρίστηκε');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα καθαρισμού');
    }
  };

  const handleUpdateProjectSettings = async (project) => {
    try {
      await axios.put(`${API}/admin/projects/${project.project_id}/notification-settings?project_type=${project.project_type}`, {
        advisor_escalation_enabled: project.advisor_escalation_enabled,
        advisor_escalation_threshold: project.advisor_escalation_threshold
      });
      toast.success('Οι ρυθμίσεις αποθηκεύτηκαν');
      setEditingProject(null);
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleResetCounter = async (project) => {
    try {
      await axios.post(`${API}/admin/projects/${project.project_id}/reset-reminder-counter?project_type=${project.project_type}`);
      toast.success('Ο μετρητής μηδενίστηκε');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  const handleSendTestReminder = async (project) => {
    try {
      await axios.post(`${API}/admin/projects/${project.project_id}/send-test-reminder?project_type=${project.project_type}`);
      toast.success('Δοκιμαστικό email στην ουρά');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποστολής');
    }
  };

  // Project Row Component
  const ProjectRow = ({ project, showActions }) => (
    <div className={`p-4 ${showActions ? 'hover:bg-slate-50' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${showActions ? 'text-slate-900' : 'text-slate-600'}`}>{project.project_title}</h3>
            {project.notification_enabled && showActions ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                <Bell className="h-3 w-3 inline mr-1" />
                Ενεργές
              </span>
            ) : showActions && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                <BellOff className="h-3 w-3 inline mr-1" />
                Ανενεργές
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">{project.client_name}</p>
          
          {showActions && (
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              {project.pending_documents > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-3 w-3" />
                  {project.pending_documents} εκκρεμή
                </span>
              )}
              {project.unanswered_reminders_count > 0 && (
                <span className="flex items-center gap-1 text-rose-600">
                  <Mail className="h-3 w-3" />
                  {project.unanswered_reminders_count} αναπάντητες
                </span>
              )}
              {project.last_reminder_sent && (
                <span>
                  Τελευταία: {new Date(project.last_reminder_sent).toLocaleDateString('el-GR')}
                </span>
              )}
            </div>
          )}

          {showActions && project.advisor_escalation_enabled && (
            <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Κλιμάκωση μετά από {project.advisor_escalation_threshold} υπενθυμίσεις
            </div>
          )}
        </div>

        {showActions && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSendTestReminder(project)}
              className="text-cyan-600"
              title="Δοκιμαστική υπενθύμιση"
            >
              <Send className="h-4 w-4" />
            </Button>
            {project.unanswered_reminders_count > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResetCounter(project)}
                className="text-amber-600"
                title="Μηδενισμός μετρητή"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingProject(editingProject?.project_id === project.project_id ? null : {...project})}
              className="text-slate-600"
              title="Ρυθμίσεις"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Edit Panel */}
      {showActions && editingProject?.project_id === project.project_id && (
        <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
          <h4 className="font-medium text-slate-900 mb-3">Ρυθμίσεις Ειδοποιήσεων</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Κλιμάκωση Συμβούλου</span>
              <Switch
                checked={editingProject.advisor_escalation_enabled}
                onCheckedChange={(checked) => setEditingProject({...editingProject, advisor_escalation_enabled: checked})}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Μετά από</span>
              <Input
                type="number"
                min="1"
                max="10"
                value={editingProject.advisor_escalation_threshold}
                onChange={(e) => setEditingProject({...editingProject, advisor_escalation_threshold: parseInt(e.target.value) || 3})}
                className="w-16"
                disabled={!editingProject.advisor_escalation_enabled}
              />
              <span className="text-sm text-slate-600">υπενθυμίσεις</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setEditingProject(null)}>
              Ακύρωση
            </Button>
            <Button size="sm" onClick={() => handleUpdateProjectSettings(editingProject)}>
              Αποθήκευση
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Φόρτωση...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Δεν βρέθηκαν δεδομένα</div>
      </div>
    );
  }

  // Separate projects by type and status
  const appProjectsActive = data.projects.filter(p => p.project_type === 'application' && p.status === 'in_progress');
  const appProjectsCompleted = data.projects.filter(p => p.project_type === 'application' && p.status !== 'in_progress');
  const implProjectsActive = data.projects.filter(p => p.project_type === 'implementation' && p.status === 'in_progress');
  const implProjectsCompleted = data.projects.filter(p => p.project_type === 'implementation' && p.status !== 'in_progress');

  // Get filtered projects based on current filter
  const filteredAppProjects = appFilter === 'active' ? appProjectsActive : appProjectsCompleted;
  const filteredImplProjects = implFilter === 'active' ? implProjectsActive : implProjectsCompleted;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Αυτόματες Ειδοποιήσεις</h1>
              <p className="text-sm text-slate-600">{data.user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              Ώρα Ελλάδας: {data.current_time_greece}
            </span>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Ανανέωση
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* User Status Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${data.user.notifications_paused ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                {data.user.notifications_paused ? (
                  <BellOff className="h-6 w-6 text-rose-600" />
                ) : (
                  <Bell className="h-6 w-6 text-emerald-600" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {data.user.notifications_paused ? 'Ειδοποιήσεις Απενεργοποιημένες' : 'Ειδοποιήσεις Ενεργές'}
                </h2>
                <p className="text-sm text-slate-600">
                  {data.user.notifications_paused 
                    ? 'Ο χρήστης δεν λαμβάνει αυτόματες ειδοποιήσεις'
                    : 'Ο χρήστης λαμβάνει αυτόματες ειδοποιήσεις για τα έργα του'
                  }
                </p>
              </div>
            </div>
            <Button
              variant={data.user.notifications_paused ? 'default' : 'outline'}
              onClick={handleToggleUserNotifications}
              className={data.user.notifications_paused ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-rose-600 border-rose-300'}
            >
              {data.user.notifications_paused ? 'Ενεργοποίηση' : 'Απενεργοποίηση'}
            </Button>
          </div>
        </div>

        {/* Email Queue Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Mail className="h-5 w-5 text-cyan-600" />
              Ουρά Email Χρήστη
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearUserQueue}
              disabled={data.email_queue.pending === 0}
              className="text-rose-600 border-rose-300"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Καθαρισμός ({data.email_queue.pending})
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{data.email_queue.pending}</div>
              <div className="text-sm text-amber-700">Σε Αναμονή</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{data.email_queue.sent_today}</div>
              <div className="text-sm text-emerald-700">Σήμερα</div>
            </div>
          </div>

          {data.email_queue.upcoming.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">Επόμενα Emails</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-600">Προγραμματισμένο</th>
                      <th className="px-3 py-2 text-left text-slate-600">Προς</th>
                      <th className="px-3 py-2 text-left text-slate-600">Θέμα</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.email_queue.upcoming.map((email, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2 text-slate-600">{email.scheduled_at_greece}</td>
                        <td className="px-3 py-2 text-slate-600">{email.to_email}</td>
                        <td className="px-3 py-2 text-slate-900">{email.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Application Projects Section */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Έργα Αίτησης
              </h2>
              {/* Filter Tabs */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setAppFilter('active')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    appFilter === 'active' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Σε Εξέλιξη ({appProjectsActive.length})
                </button>
                <button
                  onClick={() => setAppFilter('completed')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    appFilter === 'completed' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Ολοκληρωμένα ({appProjectsCompleted.length})
                </button>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-slate-100">
            {filteredAppProjects.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                {appFilter === 'active' ? 'Δεν υπάρχουν έργα σε εξέλιξη' : 'Δεν υπάρχουν ολοκληρωμένα έργα'}
              </div>
            ) : (
              filteredAppProjects.map(project => (
                <ProjectRow 
                  key={project.project_id} 
                  project={project} 
                  showActions={appFilter === 'active'} 
                />
              ))
            )}
          </div>
        </div>

        {/* Implementation Projects Section */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-purple-600" />
                Έργα Υλοποίησης
              </h2>
              {/* Filter Tabs */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setImplFilter('active')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    implFilter === 'active' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Σε Εξέλιξη ({implProjectsActive.length})
                </button>
                <button
                  onClick={() => setImplFilter('completed')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    implFilter === 'completed' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Ολοκληρωμένα ({implProjectsCompleted.length})
                </button>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-slate-100">
            {filteredImplProjects.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                {implFilter === 'active' ? 'Δεν υπάρχουν έργα σε εξέλιξη' : 'Δεν υπάρχουν ολοκληρωμένα έργα'}
              </div>
            ) : (
              filteredImplProjects.map(project => (
                <ProjectRow 
                  key={project.project_id} 
                  project={project} 
                  showActions={implFilter === 'active'} 
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
